import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AdminMongoRestoreConflictError,
  type AdminMongoCollectionInfo,
  fetchAdminMongoCollections,
  fetchAdminMongoDocuments,
  postAdminMongoArchiveCollection,
  postAdminMongoDuplicateCollection,
  postAdminMongoRenameCollection,
  postAdminMongoRestoreCollection,
} from "../api/adminMongoApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "react-toastify";
import { MongoBackupDialog } from "./MongoBackupDialog";
import { fetchFormConfig } from "../api/formConfigApi";
import {
  isFormSubmissionRow,
  validatedUserDetailRows,
} from "../utils/formSubmissionRowUtils";

const PAGE_SIZE = 50;
const MAX_CELL_CHARS = 500;
const ARCHIVED_PREFIX = "_archived_";
const SAFE_MONGO_COLLECTION_NAME = /^[a-zA-Z0-9_.-]+$/;

function validateMongoCollectionNewName(currentName: string, raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "Enter a collection name";
  }
  if (trimmed.length > 200) {
    return "Name is too long (max 200 characters)";
  }
  if (trimmed === currentName) {
    return "New name must differ from the current collection name";
  }
  if (!SAFE_MONGO_COLLECTION_NAME.test(trimmed)) {
    return "Use only letters, numbers, underscore, dot, and hyphen";
  }
  return null;
}

function isArchivedCollectionName(name: string): boolean {
  return name.startsWith(ARCHIVED_PREFIX);
}

function parseArchivedCollectionName(name: string): { originalName: string; timestamp?: string } | null {
  if (!name.startsWith(ARCHIVED_PREFIX)) {
    return null;
  }
  const tail = name.slice(ARCHIVED_PREFIX.length);
  if (!tail) {
    return null;
  }
  const m = /^(\d+)_(.+)$/.exec(tail);
  if (m) {
    return { originalName: m[2], timestamp: m[1] };
  }
  return { originalName: tail };
}

type CollectionListSortMode = "name" | "recent_update";

function formatCollectionMaxUpdatedIso(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const t = Date.parse(iso);
  if (Number.isNaN(t)) {
    return "—";
  }
  return new Date(t).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatArchivedAtSubtitle(timestampMs: string): string {
  const ms = Number(timestampMs);
  if (!Number.isFinite(ms)) {
    return timestampMs;
  }
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) {
    return timestampMs;
  }
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

type DocViewMode = "table" | "json";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function sortColumnKeys(keys: string[]): string[] {
  const priority = ["_id", "uid", "__v"];
  const head = priority.filter((k) => keys.includes(k));
  const tail = keys.filter((k) => !priority.includes(k)).sort((a, b) => a.localeCompare(b));
  return [...head, ...tail];
}

function columnsForDocuments(documents: unknown[]): string[] {
  const seen = new Set<string>();
  for (const doc of documents) {
    if (isRecord(doc)) {
      for (const k of Object.keys(doc)) {
        seen.add(k);
      }
    }
  }
  return sortColumnKeys([...seen]);
}

/** When any row is a form submission, show `_form` / `_formUid` / `_formTitle` first. */
function reorderFormColumnsFirst(keys: string[], documents: unknown[]): string[] {
  const anyFormSubmitted = documents.some((d) => isFormSubmissionRow(d));
  if (!anyFormSubmitted || keys.length === 0) {
    return keys;
  }
  const formKeys = ["_form", "_formUid", "_formTitle"];
  const head = formKeys.filter((k) => keys.includes(k));
  if (head.length === 0) {
    return keys;
  }
  const rest = keys.filter((k) => !head.includes(k));
  return [...head, ...rest];
}

function isMongoFormColumnKey(col: string): boolean {
  return col === "_form" || col === "_formUid" || col === "_formTitle";
}

function stringifyDocument(doc: unknown): string {
  try {
    return JSON.stringify(doc, null, 2);
  } catch {
    return String(doc);
  }
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value.length > MAX_CELL_CHARS ? `${value.slice(0, MAX_CELL_CHARS)}…` : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    const s = JSON.stringify(value);
    return s.length > MAX_CELL_CHARS ? `${s.slice(0, MAX_CELL_CHARS)}…` : s;
  } catch {
    return String(value);
  }
}

const MongoCollectionsInspector = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const collectionFromUrl = searchParams.get("collection");
  const filterFromUrl = searchParams.get("filter");
  const lastAppliedMongoLinkRef = useRef<string | null>(null);
  const [database, setDatabase] = useState<string | null>(null);
  const [collectionRows, setCollectionRows] = useState<AdminMongoCollectionInfo[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);

  const [collectionFilter, setCollectionFilter] = useState("");
  const [collectionListSort, setCollectionListSort] = useState<CollectionListSortMode>("name");
  const [showCollectionMaxUpdatedAt, setShowCollectionMaxUpdatedAt] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const [queryFilterInput, setQueryFilterInput] = useState("{}");
  const [activeFilterJson, setActiveFilterJson] = useState("{}");
  const [sortField, setSortField] = useState("_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [formUserDialogDoc, setFormUserDialogDoc] = useState<Record<string, unknown> | null>(null);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [archiveView, setArchiveView] = useState(false);
  const [collectionActionName, setCollectionActionName] = useState<string | null>(null);
  const [restoreConflict, setRestoreConflict] = useState<{
    archivedName: string;
    existingCollection: string;
  } | null>(null);
  const [duplicateSourceName, setDuplicateSourceName] = useState<string | null>(null);
  const [duplicateTargetInput, setDuplicateTargetInput] = useState("");
  const [renameSourceName, setRenameSourceName] = useState<string | null>(null);
  const [renameTargetInput, setRenameTargetInput] = useState("");

  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [documents, setDocuments] = useState<unknown[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [docViewMode, setDocViewMode] = useState<DocViewMode>("table");
  const [detailDoc, setDetailDoc] = useState<unknown | null>(null);
  const [detailViewMode, setDetailViewMode] = useState<DocViewMode>("json");
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);

  const copyCellToClipboard = useCallback(async (fullText: string, displayText: string) => {
    const text = fullText !== "" ? fullText : displayText;
    if (text === "") {
      toast.info("Nothing to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }, []);

  const copyDocumentToClipboard = useCallback(async (doc: unknown) => {
    const text = stringifyDocument(doc);
    if (text === "") {
      toast.info("Nothing to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied document to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }, []);

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    setCollectionsError(null);
    try {
      const res = await fetchAdminMongoCollections();
      setDatabase(res.database ?? null);
      setCollectionRows(res.collections ?? []);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load collections";
      setCollectionsError(message);
      setCollectionRows([]);
      setDatabase(null);
      toast.error(message);
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(
    async (name: string, nextSkip: number) => {
      setDocsLoading(true);
      setDocsError(null);
      try {
        const res = await fetchAdminMongoDocuments(name, {
          limit: PAGE_SIZE,
          skip: nextSkip,
          filterJson: activeFilterJson,
          sortField,
          sortOrder,
        });
        setTotal(typeof res.total === "number" ? res.total : 0);
        setSkip(typeof res.skip === "number" ? res.skip : nextSkip);
        setDocuments(Array.isArray(res.documents) ? res.documents : []);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to load documents";
        setDocsError(message);
        setDocuments([]);
        setTotal(0);
        toast.error(message);
      } finally {
        setDocsLoading(false);
      }
    },
    [activeFilterJson, sortField, sortOrder]
  );

  const refreshCollectionsAndDocuments = useCallback(async () => {
    await loadCollections();
    if (selected) {
      await loadDocuments(selected, skip);
    }
  }, [loadCollections, loadDocuments, selected, skip]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    if (!collectionFromUrl || collectionRows.length === 0) {
      return;
    }
    const key = `${collectionFromUrl}\0${filterFromUrl ?? ""}`;
    if (lastAppliedMongoLinkRef.current === key) {
      return;
    }
    const exists = collectionRows.some((r) => r.name === collectionFromUrl);
    if (!exists) {
      return;
    }
    lastAppliedMongoLinkRef.current = key;
    setSelected(collectionFromUrl);
    if (filterFromUrl) {
      try {
        const parsed: unknown = JSON.parse(filterFromUrl);
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          setActiveFilterJson(filterFromUrl);
          setQueryFilterInput(filterFromUrl);
        }
      } catch {
        // ignore invalid filter query param
      }
    }
    setSkip(0);
  }, [collectionFromUrl, filterFromUrl, collectionRows]);

  useEffect(() => {
    if (!selected) {
      setDocuments([]);
      setTotal(0);
      setSkip(0);
      return;
    }
    loadDocuments(selected, skip);
  }, [selected, skip, activeFilterJson, sortField, sortOrder, loadDocuments]);

  useEffect(() => {
    if (detailDoc != null) {
      setDetailViewMode("json");
    }
  }, [detailDoc]);

  useEffect(() => {
    setHighlightedRowKey(null);
  }, [documents]);

  const applyQueryFilter = useCallback(() => {
    try {
      const parsed: unknown = JSON.parse(queryFilterInput);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        toast.error("Filter must be a JSON object");
        return;
      }
      setActiveFilterJson(JSON.stringify(parsed));
      setSkip(0);
      setFilterDialogOpen(false);
    } catch {
      toast.error("Invalid filter JSON");
    }
  }, [queryFilterInput]);

  const clearQueryFilter = useCallback(() => {
    setQueryFilterInput("{}");
    setActiveFilterJson("{}");
    setSkip(0);
  }, []);

  const filterIsActive = useMemo(() => {
    if (activeFilterJson.trim() === "" || activeFilterJson.trim() === "{}") {
      return false;
    }
    try {
      const parsed: unknown = JSON.parse(activeFilterJson);
      return parsed != null && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed as object).length > 0;
    } catch {
      return true;
    }
  }, [activeFilterJson]);

  const handleSortHeaderClick = useCallback(
    (col: string) => {
      if (sortField === col) {
        setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
      } else {
        setSortField(col);
        setSortOrder("desc");
      }
      setSkip(0);
    },
    [sortField]
  );

  const collectionsForView = useMemo(() => {
    return collectionRows.filter((row) =>
      archiveView ? isArchivedCollectionName(row.name) : !isArchivedCollectionName(row.name)
    );
  }, [collectionRows, archiveView]);

  const filteredCollectionRows = useMemo(() => {
    const q = collectionFilter.trim().toLowerCase();
    if (!q) {
      return collectionsForView;
    }
    return collectionsForView.filter((row) => row.name.toLowerCase().includes(q));
  }, [collectionsForView, collectionFilter]);

  const displaySortedCollectionRows = useMemo(() => {
    const rows = [...filteredCollectionRows];
    if (collectionListSort === "name") {
      rows.sort((a, b) => a.name.localeCompare(b.name));
      return rows;
    }
    rows.sort((a, b) => {
      const ta = a.maxUpdatedAt ? Date.parse(a.maxUpdatedAt) : NaN;
      const tb = b.maxUpdatedAt ? Date.parse(b.maxUpdatedAt) : NaN;
      const va = Number.isFinite(ta);
      const vb = Number.isFinite(tb);
      if (va && vb && tb !== ta) {
        return tb - ta;
      }
      if (va && !vb) {
        return -1;
      }
      if (!va && vb) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [filteredCollectionRows, collectionListSort]);

  useEffect(() => {
    if (selected != null && !collectionsForView.some((r) => r.name === selected)) {
      setSelected(null);
    }
  }, [archiveView, collectionsForView, selected]);

  const collectionVisibleInView = useCallback(
    (collectionName: string, viewArchived: boolean) => {
      return viewArchived
        ? isArchivedCollectionName(collectionName)
        : !isArchivedCollectionName(collectionName);
    },
    []
  );

  const handleArchiveCollection = useCallback(
    async (name: string) => {
      setCollectionActionName(name);
      try {
        const res = await postAdminMongoArchiveCollection(name);
        const next = res.newName ?? null;
        toast.success(next ? `Archived as ${next}` : "Archived");
        if (selected === name) {
          setSelected(next && collectionVisibleInView(next, archiveView) ? next : null);
        }
        await loadCollections();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Archive failed";
        toast.error(message);
      } finally {
        setCollectionActionName(null);
      }
    },
    [archiveView, collectionVisibleInView, loadCollections, selected]
  );

  const openDuplicateDialog = useCallback((sourceName: string) => {
    setDuplicateSourceName(sourceName);
    setDuplicateTargetInput(`${sourceName}_`);
  }, []);

  const closeDuplicateDialog = useCallback(() => {
    setDuplicateSourceName(null);
    setDuplicateTargetInput("");
  }, []);

  const submitDuplicateDialog = useCallback(async () => {
    if (!duplicateSourceName) {
      return;
    }
    const err = validateMongoCollectionNewName(duplicateSourceName, duplicateTargetInput);
    if (err) {
      toast.error(err);
      return;
    }
    const targetName = duplicateTargetInput.trim();
    setCollectionActionName(duplicateSourceName);
    try {
      const res = await postAdminMongoDuplicateCollection(duplicateSourceName, { targetName });
      const next = res.newName ?? null;
      toast.success(next ? `Duplicated to ${next}` : "Duplicated");
      closeDuplicateDialog();
      if (next && collectionVisibleInView(next, archiveView)) {
        setSelected(next);
      }
      await loadCollections();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Duplicate failed";
      toast.error(message);
    } finally {
      setCollectionActionName(null);
    }
  }, [
    archiveView,
    closeDuplicateDialog,
    collectionVisibleInView,
    duplicateSourceName,
    duplicateTargetInput,
    loadCollections,
  ]);

  const openRenameDialog = useCallback((currentName: string) => {
    setRenameSourceName(currentName);
    setRenameTargetInput(currentName);
  }, []);

  const closeRenameDialog = useCallback(() => {
    setRenameSourceName(null);
    setRenameTargetInput("");
  }, []);

  const submitRenameDialog = useCallback(async () => {
    if (!renameSourceName) {
      return;
    }
    const err = validateMongoCollectionNewName(renameSourceName, renameTargetInput);
    if (err) {
      toast.error(err);
      return;
    }
    const newName = renameTargetInput.trim();
    setCollectionActionName(renameSourceName);
    try {
      const res = await postAdminMongoRenameCollection(renameSourceName, { newName });
      const next = res.newName ?? null;
      toast.success(next ? `Renamed to ${next}` : "Renamed");
      closeRenameDialog();
      if (selected === renameSourceName && next) {
        setSelected(collectionVisibleInView(next, archiveView) ? next : null);
      }
      await loadCollections();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Rename failed";
      toast.error(message);
    } finally {
      setCollectionActionName(null);
    }
  }, [
    archiveView,
    closeRenameDialog,
    collectionVisibleInView,
    loadCollections,
    renameSourceName,
    renameTargetInput,
    selected,
  ]);

  const handleRestoreCollection = useCallback(
    async (name: string, archiveExistingFirst = false) => {
      setCollectionActionName(name);
      try {
        const res = await postAdminMongoRestoreCollection(name, { archiveExistingFirst });
        const next = res.newName ?? null;
        toast.success(next ? `Restored as ${next}` : "Restored");
        if (selected === name) {
          setSelected(next && collectionVisibleInView(next, archiveView) ? next : null);
        }
        await loadCollections();
      } catch (e) {
        if (!archiveExistingFirst && e instanceof AdminMongoRestoreConflictError) {
          setRestoreConflict({
            archivedName: name,
            existingCollection: e.existingCollection,
          });
          return;
        }
        const message = e instanceof Error ? e.message : "Restore failed";
        toast.error(message);
      } finally {
        setCollectionActionName(null);
      }
    },
    [archiveView, collectionVisibleInView, loadCollections, selected]
  );

  const confirmRestoreArchiveConflict = useCallback(() => {
    if (!restoreConflict) {
      return;
    }
    const { archivedName } = restoreConflict;
    setRestoreConflict(null);
    void handleRestoreCollection(archivedName, true);
  }, [handleRestoreCollection, restoreConflict]);

  const jsonBlock = useMemo(() => {
    if (documents.length === 0) {
      return "[]";
    }
    try {
      return JSON.stringify(documents, null, 2);
    } catch {
      return String(documents);
    }
  }, [documents]);

  const tableColumns = useMemo(() => {
    const base = columnsForDocuments(documents);
    return reorderFormColumnsFirst(base, documents);
  }, [documents]);

  const openPublicFormForSubmission = useCallback(async (doc: unknown) => {
    if (!isRecord(doc)) {
      return;
    }
    const uid = String(doc._formUid ?? "").trim();
    if (!uid) {
      toast.error("This row has no form reference");
      return;
    }
    try {
      const cfg = await fetchFormConfig(uid);
      const token = cfg.publicToken?.trim();
      if (!token) {
        toast.error("This form has no public token");
        return;
      }
      window.open(`/forms/${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load form";
      toast.error(message);
    }
  }, []);

  const tableRows = useMemo(() => {
    if (tableColumns.length === 0 && documents.length > 0) {
      return documents.map((doc, i) => ({
        key: `row-${i}`,
        cells: [formatCellValue(doc)],
        titles: [formatCellValue(doc)],
        doc,
      }));
    }
    return documents.map((doc, i) => {
      if (!isRecord(doc)) {
        let full: string;
        try {
          full = JSON.stringify(doc);
        } catch {
          full = String(doc);
        }
        const display = formatCellValue(doc);
        const cells = tableColumns.map((_, idx) => (idx === 0 ? display : ""));
        const titles = tableColumns.map((_, idx) => (idx === 0 ? full : ""));
        return { key: `row-${i}`, cells, titles, doc };
      }
      const titles = tableColumns.map((col) => {
        try {
          return doc[col] === undefined ? "" : JSON.stringify(doc[col]);
        } catch {
          return String(doc[col]);
        }
      });
      const cells = tableColumns.map((col) => formatCellValue(doc[col]));
      return { key: `row-${i}`, cells, titles, doc };
    });
  }, [documents, tableColumns]);

  const detailDialogJson = useMemo(
    () => (detailDoc == null ? "" : stringifyDocument(detailDoc)),
    [detailDoc]
  );

  const detailDialogColumns = useMemo(() => {
    if (detailDoc == null) {
      return [] as string[];
    }
    if (!isRecord(detailDoc)) {
      return [] as string[];
    }
    return sortColumnKeys(Object.keys(detailDoc));
  }, [detailDoc]);

  const canPrev = selected != null && skip > 0;
  const canNext = selected != null && skip + documents.length < total;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-3 p-4 max-w-[1600px] mx-auto w-full box-border">
      <div className="shrink-0 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            MongoDB collections
          </h1>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-1">
            <p>Admin-only browser{database ? ` (${database})` : ""}</p>
            <p>
              <code className="text-xs">View matches collection.find(filter)</code>.
            </p>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={() => setBackupDialogOpen(true)}>
            Backup
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={collectionsLoading}
            onClick={() => void refreshCollectionsAndDocuments()}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[minmax(240px,320px)_1fr] lg:grid-rows-1 gap-4">
        <Card className="flex min-h-0 h-full max-h-full flex-col overflow-hidden">
          <CardHeader className="pb-2 space-y-2 shrink-0">
            <CardTitle className="text-base">Collections</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">View</span>
              <div className="flex rounded-md border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900">
                <Button
                  type="button"
                  variant={!archiveView ? "default" : "ghost"}
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setArchiveView(false)}
                >
                  Active
                </Button>
                <Button
                  type="button"
                  variant={archiveView ? "default" : "ghost"}
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setArchiveView(true)}
                >
                  Archived
                </Button>
              </div>
            </div>
            <Input
              placeholder="Filter collection names…"
              value={collectionFilter}
              onChange={(e) => setCollectionFilter(e.target.value)}
              className="h-9"
            />
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-muted-foreground shrink-0">Sort</span>
                <Select
                  value={collectionListSort}
                  onValueChange={(v) => setCollectionListSort(v as CollectionListSortMode)}
                >
                  <SelectTrigger id="mongo-collections-sort" className="h-9 flex-1 min-w-0">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="recent_update">Recent update</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="mongo-collections-show-updated"
                  checked={showCollectionMaxUpdatedAt}
                  onCheckedChange={setShowCollectionMaxUpdatedAt}
                />
                <Label
                  htmlFor="mongo-collections-show-updated"
                  className="text-xs font-normal cursor-pointer leading-snug"
                >
                  Show max activity{" "}
                  <span className="text-muted-foreground">
                    (<code className="text-[10px] align-middle">_updatedAt</code>
                    {" / "}
                    <code className="text-[10px] align-middle">updatedAt</code>
                    {" / "}
                    <code className="text-[10px] align-middle">_createdAt</code>
                    {" / "}
                    <code className="text-[10px] align-middle">createdAt</code>
                    {" / "}
                    <code className="text-[10px] align-middle">updated</code>
                    {" / "}
                    <code className="text-[10px] align-middle">at</code>)
                  </span>
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
            {collectionsError ? (
              <p className="text-sm text-red-600 dark:text-red-400 shrink-0">{collectionsError}</p>
            ) : null}
            <div className="flex-1 min-h-0 rounded-md border border-gray-200 dark:border-gray-700 overflow-y-auto overscroll-contain">
              <ul className="p-1 text-sm">
                {collectionsLoading && collectionRows.length === 0 ? (
                  <li className="px-2 py-2 text-gray-500">Loading…</li>
                ) : null}
                {displaySortedCollectionRows.map((row) => {
                  const { name, count, maxUpdatedAt } = row;
                  const archivedParsed = archiveView ? parseArchivedCollectionName(name) : null;
                  const actionBusy = collectionActionName != null || collectionsLoading;
                  return (
                    <ContextMenu key={name}>
                      <ContextMenuTrigger asChild>
                        <li className="flex gap-1 items-center min-w-0 py-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              setSelected(name);
                              setQueryFilterInput("{}");
                              setActiveFilterJson("{}");
                              setSortField("_id");
                              setSortOrder("desc");
                              setSkip(0);
                            }}
                            className={`flex flex-1 min-w-0 items-center gap-2 text-left px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 ${selected === name
                              ? "bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-100"
                              : "text-gray-800 dark:text-gray-200"
                              }`}
                          >
                            <span className="min-w-0 flex-1 text-left">
                              {archiveView && archivedParsed ? (
                                <span className="flex flex-col items-start min-w-0 text-left gap-0.5">
                                  <span className="font-mono text-sm truncate w-full">{archivedParsed.originalName}</span>
                                  {archivedParsed.timestamp ? (
                                    <span className="text-xs text-muted-foreground truncate w-full">
                                      {formatArchivedAtSubtitle(archivedParsed.timestamp)}
                                    </span>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="font-mono text-sm truncate block">{name}</span>
                              )}
                            </span>
                            <span className="shrink-0 flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                              <span className="tabular-nums" title="Documents (estimated)">
                                {count.toLocaleString()}
                              </span>
                              {showCollectionMaxUpdatedAt ? (
                                <span
                                  className="tabular-nums max-w-[10rem] truncate text-[10px] leading-tight"
                                  title="Max per document: _updatedAt, updatedAt, _createdAt, createdAt, updated, at (first non-null each)"
                                >
                                  {formatCollectionMaxUpdatedIso(maxUpdatedAt)}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-56">
                        <ContextMenuItem
                          disabled={actionBusy}
                          onSelect={() => openDuplicateDialog(name)}
                        >
                          Duplicate
                        </ContextMenuItem>
                        <ContextMenuItem
                          disabled={actionBusy}
                          onSelect={() => openRenameDialog(name)}
                        >
                          Rename
                        </ContextMenuItem>
                        {!archiveView ? (
                          <ContextMenuItem
                            disabled={actionBusy}
                            onSelect={() => void handleArchiveCollection(name)}
                          >
                            Archive
                          </ContextMenuItem>
                        ) : (
                          <ContextMenuItem
                            disabled={actionBusy}
                            onSelect={() => void handleRestoreCollection(name)}
                          >
                            Restore
                          </ContextMenuItem>
                        )}
                      </ContextMenuContent>
                    </ContextMenu>
                  );
                })}
                {!collectionsLoading &&
                  filteredCollectionRows.length === 0 &&
                  collectionsForView.length > 0 ? (
                  <li className="px-2 py-2 text-gray-500">No matches</li>
                ) : null}
                {!collectionsLoading &&
                  collectionsForView.length === 0 &&
                  collectionRows.length > 0 ? (
                  <li className="px-2 py-2 text-gray-500">
                    {archiveView ? "No archived collections" : "No active collections"}
                  </li>
                ) : null}
                {!collectionsLoading && collectionRows.length === 0 && !collectionsError ? (
                  <li className="px-2 py-2 text-gray-500">No collections</li>
                ) : null}
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="flex min-h-0 h-full max-h-full flex-1 flex-col overflow-hidden">
          <CardHeader className="pb-2 shrink-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">
                {selected ? (
                  <>
                    <span className="font-mono text-sm">{selected}</span>
                    <span className="text-gray-500 font-normal text-sm ml-2">
                      {total} document{total === 1 ? "" : "s"}
                    </span>
                    {filterIsActive ? (
                      <Badge variant="secondary" className="ml-2 align-middle font-normal">
                        Filter on
                      </Badge>
                    ) : null}
                  </>
                ) : (
                  "Select a collection"
                )}
              </CardTitle>
              {selected ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setFilterDialogOpen(true)}
                  >
                    Filter…
                  </Button>
                  <div className="flex rounded-md border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900">
                    <Button
                      type="button"
                      variant={docViewMode === "table" ? "default" : "ghost"}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setDocViewMode("table")}
                    >
                      Table
                    </Button>
                    <Button
                      type="button"
                      variant={docViewMode === "json" ? "default" : "ghost"}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setDocViewMode("json")}
                    >
                      JSON
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canPrev || docsLoading}
                      onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-gray-500 tabular-nums">
                      {total === 0 ? 0 : skip + 1}–{Math.min(skip + documents.length, total)} of{" "}
                      {total}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canNext || docsLoading}
                      onClick={() => setSkip(skip + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
            {docsError ? (
              <p className="text-sm text-red-600 dark:text-red-400 mb-2 shrink-0">{docsError}</p>
            ) : null}
            {docsLoading ? (
              <p className="text-sm text-gray-500 mb-2 shrink-0">Loading documents…</p>
            ) : null}
            <div className="flex-1 min-h-0 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 flex flex-col overflow-hidden">
              {!selected ? (
                <p className="p-3 text-sm text-gray-600 dark:text-gray-400 shrink-0">
                  Choose a collection from the list.
                </p>
              ) : docViewMode === "json" ? (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
                  <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-all text-gray-900 dark:text-gray-100">
                    {jsonBlock}
                  </pre>
                </div>
              ) : documents.length === 0 ? (
                <p className="p-3 text-sm text-gray-600 dark:text-gray-400 shrink-0">
                  No documents on this page.
                </p>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
                  <Table className="text-xs w-max min-w-full">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        {(tableColumns.length === 0 ? ["document"] : tableColumns).map((col) => {
                          const headerSortable = tableColumns.length > 0;
                          return (
                            <TableHead
                              key={col}
                              className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-900 font-mono whitespace-nowrap min-w-[120px] max-w-[320px] p-0 border-b border-gray-200 dark:border-gray-700"
                            >
                              {headerSortable ? (
                                <button
                                  type="button"
                                  className={`w-full h-10 px-2 text-left align-middle hover:bg-gray-200/80 dark:hover:bg-gray-800/80 ${sortField === col
                                    ? "text-blue-700 dark:text-blue-300"
                                    : "text-foreground"
                                    }`}
                                  onClick={() => handleSortHeaderClick(col)}
                                >
                                  {col}
                                  {sortField === col ? (sortOrder === "desc" ? " \u25BC" : " \u25B2") : ""}
                                </button>
                              ) : (
                                <span className="flex h-10 items-center px-2 text-muted-foreground">{col}</span>
                              )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableRows.map((row) => {
                        const colNames = tableColumns.length === 0 ? ["document"] : tableColumns;
                        return (
                          <TableRow
                            key={row.key}
                            data-selected={highlightedRowKey === row.key ? true : undefined}
                            className={cn(
                              "cursor-default transition-colors",
                              highlightedRowKey === row.key
                                ? "bg-blue-100/90 dark:bg-blue-950/45"
                                : "hover:bg-muted/25 dark:hover:bg-gray-800/40"
                            )}
                            onDoubleClick={() => {
                              setHighlightedRowKey(row.key);
                              setDetailDoc(row.doc);
                            }}
                          >
                            {row.cells.map((cell, ci) => {
                              const full = row.titles[ci] ?? "";
                              const fieldLabel = colNames[ci] ?? `column ${ci}`;
                              const showFormCellActions =
                                isMongoFormColumnKey(fieldLabel) && isFormSubmissionRow(row.doc);
                              return (
                                <ContextMenu key={`${row.key}-${ci}`}>
                                  <ContextMenuTrigger asChild>
                                    <TableCell
                                      className={cn(
                                        "font-mono align-top max-w-[320px] cursor-pointer transition-colors",
                                        highlightedRowKey === row.key
                                          ? "hover:bg-blue-200/50 dark:hover:bg-blue-900/35"
                                          : "hover:bg-muted/60 dark:hover:bg-gray-800/80"
                                      )}
                                      title={full || undefined}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setHighlightedRowKey(row.key)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          setHighlightedRowKey(row.key);
                                        }
                                      }}
                                    >
                                      <span className="line-clamp-4 break-all whitespace-pre-wrap">{cell}</span>
                                    </TableCell>
                                  </ContextMenuTrigger>
                                  <ContextMenuContent className="w-56">
                                    {showFormCellActions ? (
                                      <>
                                        <ContextMenuItem
                                          onSelect={() => {
                                            setHighlightedRowKey(row.key);
                                            if (isRecord(row.doc)) {
                                              setFormUserDialogDoc(row.doc);
                                            }
                                          }}
                                        >
                                          Show user
                                        </ContextMenuItem>
                                        <ContextMenuItem
                                          onSelect={() => {
                                            setHighlightedRowKey(row.key);
                                            void openPublicFormForSubmission(row.doc);
                                          }}
                                        >
                                          Show form
                                        </ContextMenuItem>
                                        <ContextMenuItem
                                          onSelect={() => {
                                            setHighlightedRowKey(row.key);
                                            navigate("/admin/logs");
                                          }}
                                        >
                                          Show logs
                                        </ContextMenuItem>
                                        <ContextMenuSeparator />
                                      </>
                                    ) : null}
                                    <ContextMenuItem
                                      onSelect={() => {
                                        setHighlightedRowKey(row.key);
                                        void copyCellToClipboard(full, cell);
                                      }}
                                    >
                                      Copy &ldquo;{fieldLabel}&rdquo; value
                                    </ContextMenuItem>
                                    <ContextMenuItem
                                      onSelect={() => {
                                        setHighlightedRowKey(row.key);
                                        void copyDocumentToClipboard(row.doc);
                                      }}
                                    >
                                      Copy document (JSON)
                                    </ContextMenuItem>
                                    <ContextMenuSeparator />
                                    <ContextMenuItem
                                      onSelect={() => {
                                        setHighlightedRowKey(row.key);
                                        setDetailDoc(row.doc);
                                      }}
                                    >
                                      Open document…
                                    </ContextMenuItem>
                                  </ContextMenuContent>
                                </ContextMenu>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <MongoBackupDialog
        open={backupDialogOpen}
        onOpenChange={setBackupDialogOpen}
        serverCollections={collectionRows.map((r) => r.name)}
      />

      <Dialog
        open={duplicateSourceName != null}
        onOpenChange={(open) => {
          if (!open) {
            closeDuplicateDialog();
          }
        }}
      >
        <DialogContent className="max-w-[min(96vw,28rem)] dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Duplicate collection</DialogTitle>
            <DialogDescription>
              The new name must differ from the source. Placeholder shows the current name; the field starts with the
              source name and an underscore.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="mongo-duplicate-target">New collection name</Label>
            <Input
              id="mongo-duplicate-target"
              spellCheck={false}
              placeholder={duplicateSourceName ?? ""}
              value={duplicateTargetInput}
              onChange={(e) => setDuplicateTargetInput(e.target.value)}
              className="font-mono h-9"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeDuplicateDialog}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void submitDuplicateDialog()}>
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renameSourceName != null}
        onOpenChange={(open) => {
          if (!open) {
            closeRenameDialog();
          }
        }}
      >
        <DialogContent className="max-w-[min(96vw,28rem)] dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Rename collection</DialogTitle>
            <DialogDescription>
              Change the collection name. The new name must differ from the current one and cannot match an existing
              collection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="mongo-rename-target">New name</Label>
            <Input
              id="mongo-rename-target"
              spellCheck={false}
              placeholder={renameSourceName ?? ""}
              value={renameTargetInput}
              onChange={(e) => setRenameTargetInput(e.target.value)}
              className="font-mono h-9"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeRenameDialog}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void submitRenameDialog()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={restoreConflict != null}
        onOpenChange={(open) => {
          if (!open) {
            setRestoreConflict(null);
          }
        }}
      >
        <DialogContent className="max-w-[min(96vw,28rem)] dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Name already in use</DialogTitle>
            <DialogDescription>
              Active collection{" "}
              <code className="text-xs font-mono">{restoreConflict?.existingCollection}</code> already
              exists. Archive it first as{" "}
              <code className="text-xs font-mono">{ARCHIVED_PREFIX}</code>
              <code className="text-xs font-mono">{"<timestamp>_<name>"}</code>, then restore{" "}
              <code className="text-xs font-mono">{restoreConflict?.archivedName}</code> to{" "}
              <code className="text-xs font-mono">{restoreConflict?.existingCollection}</code>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setRestoreConflict(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmRestoreArchiveConflict}>
              Archive existing and restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={filterDialogOpen}
        onOpenChange={(open) => {
          setFilterDialogOpen(open);
          if (open) {
            setQueryFilterInput(activeFilterJson);
          }
        }}
      >
        <DialogContent className="max-w-[min(96vw,32rem)] dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Query filter</DialogTitle>
            <DialogDescription>
              MongoDB find filter as a JSON object (same as{" "}
              <code className="text-xs">collection.find(filter)</code>). Use{" "}
              <code className="text-xs">{"{}"}</code> for no filter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="mongo-query-filter-dialog">Filter JSON</Label>
            <textarea
              id="mongo-query-filter-dialog"
              spellCheck={false}
              value={queryFilterInput}
              onChange={(e) => setQueryFilterInput(e.target.value)}
              className="w-full min-h-[140px] rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:border-gray-600"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setFilterDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearQueryFilter();
                setFilterDialogOpen(false);
              }}
            >
              Clear and apply
            </Button>
            <Button type="button" onClick={applyQueryFilter}>
              Apply filter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDoc != null} onOpenChange={(open) => !open && setDetailDoc(null)}>
        <DialogContent className="max-w-[min(96vw,56rem)] max-h-[min(85vh,900px)] flex flex-col gap-3 overflow-hidden dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Document</DialogTitle>
            <DialogDescription>
              Double-click a row or use the cell context menu to open. JSON is pretty-printed; table
              shows top-level fields.
            </DialogDescription>
          </DialogHeader>
          <div className="flex shrink-0 flex-wrap items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900 w-fit">
            <Button
              type="button"
              variant={detailViewMode === "json" ? "default" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => setDetailViewMode("json")}
            >
              JSON
            </Button>
            <Button
              type="button"
              variant={detailViewMode === "table" ? "default" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => setDetailViewMode("table")}
            >
              Table
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 ml-1"
              onClick={() => detailDoc != null && void copyDocumentToClipboard(detailDoc)}
            >
              Copy JSON
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            {detailDoc == null ? null : detailViewMode === "json" ? (
              <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-all text-gray-900 dark:text-gray-100">
                {detailDialogJson}
              </pre>
            ) : !isRecord(detailDoc) ? (
              <div className="p-3 text-sm text-muted-foreground">
                <div className="font-medium text-foreground mb-2">Value</div>
                <pre className="text-xs font-mono whitespace-pre-wrap break-all">{detailDialogJson}</pre>
              </div>
            ) : detailDialogColumns.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">Empty object</p>
            ) : (
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="sticky top-0 z-10 w-1/3 bg-gray-100 dark:bg-gray-900 font-mono">
                      Field
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-900 font-mono">
                      Value
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailDialogColumns.map((key) => {
                    const val = detailDoc[key];
                    const display = formatCellValue(val);
                    return (
                      <TableRow key={key}>
                        <TableCell className="font-mono align-top text-muted-foreground">{key}</TableCell>
                        <TableCell className="font-mono align-top whitespace-pre-wrap break-all">
                          {display}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={formUserDialogDoc != null}
        onOpenChange={(open) => {
          if (!open) {
            setFormUserDialogDoc(null);
          }
        }}
      >
        <DialogContent className="max-w-[min(96vw,32rem)] dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle>Validated user</DialogTitle>
            <DialogDescription>
              Submitter and ownership fields stored on this document (public form submission metadata).
            </DialogDescription>
          </DialogHeader>
          {formUserDialogDoc == null ? null : (
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[40%] font-mono">Field</TableHead>
                  <TableHead className="font-mono">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {validatedUserDetailRows(formUserDialogDoc).map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className="align-top text-muted-foreground font-medium">{row.label}</TableCell>
                    <TableCell className="font-mono align-top whitespace-pre-wrap break-all">{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFormUserDialogDoc(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MongoCollectionsInspector;
